// Sagas for user experience interactions in dashboard
// (And for miscellaneous features that doesn't fit elsewhere)

import {
  fork,
  join,
  take,
  takeEvery,
  put,
  select,
  delay,
  call,
} from 'redux-saga/effects'
import * as R from 'ramda'
import { t } from 'ttag'

import { getUser } from 'ducks/auth'
import {
  INIT_SNACKS_QUEUE,
  SEND_MESSAGE,
  CREATE_MESSAGE,
  IMPORT_CANDIDATES,
  PADDLE_CHECKOUT,
  showModal,
  SHOW_MODAL,
  closeSnackbar,
  showSnackbar,
  getSnacks,
  initSnacksQueue,
} from 'ducks/ux'
import { errorAction } from 'ducks/error'
import { getLocation, toTemplatesCreator } from 'ducks/router'
import { accounts, candidates, interviewTemplates, templates } from 'orm'
import {
  DELETE_ERROR,
  DELETE_SUCCESS,
  POST_ERROR,
  POST_SUCCESS,
  PATCH_ERROR,
  PATCH_SUCCESS,
  TRASH,
} from 'orm/actionTypes'
import Api from 'utils/api'
import loadPaddle from 'utils/loadPaddle'
import { renderMessage } from 'utils/messages'

export default function* uxSaga() {
  yield takeEvery(TRASH, trashConfirmSaga)
  yield takeEvery(SEND_MESSAGE, sendMessageSaga)
  yield takeEvery(IMPORT_CANDIDATES, importCandidatesSaga)
  yield takeEvery(CREATE_MESSAGE, createMessageSaga)
  yield takeEvery(INIT_SNACKS_QUEUE, initSnacksQueueSaga)
  yield takeEvery(SHOW_MODAL, refreshTemplates)
  yield takeEvery(SHOW_MODAL, clickWindow)
  yield takeEvery(PADDLE_CHECKOUT, paddleCheckoutSaga)
  yield takeEvery('*', routeChangeDeselectSaga)
  yield fork(snackbarListener)
}

function* clickWindow() {
  // Hacky way to close dropdown menus when a modal is shown
  yield call(() => document.documentElement.click())
}

function* refreshTemplates(action) {
  // Fetch project's templates when the send invite modal is opened
  if (R.pathEq(['payload', 'modalType'], 'SendInvite', action)) {
    const project = R.path(['payload', 'modalProps', 'project'], action)
    yield put(templates.fetchStart({ project }))
  }
  // Fetch candidate data when the interview modal is opened
  if (R.pathEq(['payload', 'modalType'], 'PerformInterview', action)) {
    const pk = R.path(['payload', 'modalProps', 'pk'], action)
    yield put(candidates.fetchStart({ pk }))
  }
}

function clearSelection() {
  // remove any user selected content from current page
  const selection = document.getSelection()
  if ((selection?.anchorNode as any)?.role === 'search') return
  selection.removeAllRanges()
}

function* routeChangeDeselectSaga(action) {
  // clear selected text or cursor on route change
  if (!/^router\//.test(action.type)) return
  yield call(clearSelection)
}

function* snackbarListener() {
  // listen for certain actions and emit snackbar messages that should be
  // displayed to the user.
  while (true) {
    const action = yield take('*')

    const payload = {
      [DELETE_ERROR]: { message: t`delete error`, type: 'error' },
      [DELETE_SUCCESS]: { message: t`delete success`, type: 'success' },
      [POST_ERROR]: { message: t`post error`, type: 'error' },
      [POST_SUCCESS]: { message: t`post success`, type: 'success' },
      [PATCH_ERROR]: { message: t`patch error`, type: 'error' },
      [PATCH_SUCCESS]: { message: t`saved`, type: 'success' },
    }[action.type]

    if (payload && action.meta.message) {
      payload.message = action.meta.message
    }

    if (!payload || action.meta.noSnackbar) continue
    yield put(initSnacksQueue(payload))
  }
}

function* autoHideSnackSaga(action) {
  const { id, timeout, type } = action.payload

  yield delay(timeout)
  yield put(closeSnackbar({ id, type }))
}

function* initSnacksQueueSaga(action) {
  const snacks = yield select(getSnacks)

  const { id, message, timeout, type } = action.payload

  if (R.equals(R.length(snacks[type]), 0)) {
    yield put(showSnackbar({ id, message, timeout, type }))
    yield autoHideSnackSaga(action)

    return
  }

  const { id: snackId, type: snackType } = R.head(snacks[type])

  yield put(closeSnackbar({ id: snackId, type: snackType }))

  yield delay(200)
  yield put(showSnackbar({ id, message, timeout, type }))
  yield autoHideSnackSaga(action)
}

function* trashConfirmSaga(action) {
  const { pks } = action.payload
  const { model } = action.meta
  yield put(showModal('ConfirmDelete', { model, pks }))
}

function* importCandidatesSaga(action) {
  let { items, project } = action.payload
  // the url path determines the project
  if (!project) project = R.path(['payload', 'pk'], yield select(getLocation))
  let ok = 0
  let fail = 0
  let duplicates = 0
  const cnn = R.values(yield select(candidates.getItems))
  for (const item of items) {
    const exists = R.find(R.propEq('email', item.email), cnn)
    if (exists) {
      duplicates++
      continue
    }
    const { response, error } = yield Api.post(['candidates'], {
      ...item,
      project,
    })
    if (response) ok++
    else {
      console.error(error)
      fail++
    }
  }
  if (duplicates)
    yield put(
      initSnacksQueue({
        message: t`${duplicates} candidates were not imported because they already exist.`,
        type: 'warning',
      }),
    )
  if (fail)
    yield put(
      initSnacksQueue({
        message: t`${fail} candidates could not be imported`,
        type: 'error',
      }),
    )
  if (ok) {
    yield put(
      initSnacksQueue({
        message: t`${ok} candidates imported`,
        type: 'success',
      }),
    )
    yield put(candidates.fetchStart({ project }))
  }
}

function* sendMessageSaga(action) {
  const { candidate, ...data } = action.payload
  const { error } = yield Api.post(['candidates', candidate.id, 'invite'], data)
  if (error) {
    yield put(errorAction(error))
  } else {
    yield put(candidates.fetchStart({ pk: candidate.id }))
    yield put(
      initSnacksQueue({
        message: t`${candidate.name} invited`,
        type: 'success',
      }),
    )
  }
}

function* createMessageSaga(action) {
  // render and send message
  const { to_candidate, message_type = 'invite.mail' } = action.payload
  const sender = yield select(getUser)
  const candidate = yield select(candidates.getItem(to_candidate))
  const templateSpec = {
    project: candidate.project,
    language: candidate.language,
    message_type,
  }
  const template = R.pipe(
    R.values,
    R.find(R.whereEq(templateSpec)),
  )(yield select(templates.getItems))
  if (!template) {
    console.error('template not found', templateSpec)
    yield put(
      initSnacksQueue({ message: t`Missing a template.`, type: 'error' }),
    )
    return
  }
  const mailData = renderMessage({
    template,
    candidate,
    sender,
  })
  const { error } = yield Api.post(
    ['candidates', to_candidate, 'invite'],
    mailData,
  )
  if (error) {
    console.error(error)
    yield put(errorAction(error))
  } else {
    yield put(candidates.fetchStart({ pk: to_candidate }))
    yield put(
      initSnacksQueue({
        message: t`${candidate.name} invited.`,
        type: 'success',
      }),
    )
  }
}

function* paddleCheckoutParameters({ plan, url, account }) {
  // https://developer.paddle.com/reference/paddle-js/parameters
  if (url) return { override: url }
  const user = yield select(getUser)
  const languageLocales = { nb: 'no', en: 'en' }
  const locale = R.propOr('en', user.language, languageLocales)
  return {
    product: plan,
    // email: user.email,
    coupon: '3EEF9D1F',
    passthrough: { account },
    locale,
  }
}

function* paddleCheckoutSaga(action) {
  // the paddle.js library cannot be bundled with webpack to comply with rules
  // we load paddle.js from the proper host with the bundled bootloader

  const loadPaddleTask = yield fork(loadPaddle)
  const Paddle = yield join(loadPaddleTask)
  const parameters = yield paddleCheckoutParameters(action.payload)
  const openPaddle = () =>
    new Promise((resolve, reject) =>
      Paddle.Checkout.open({
        ...parameters,
        closeCallback: resolve,
        successCallback: resolve,
      }),
    )
  const { checkout } = yield call(openPaddle)
  yield call(console.debug, checkout)
  if (checkout.completed) {
    yield put(initSnacksQueue({ message: `Checkout ok.`, type: 'success' }))
    yield delay(5000)
    yield put(accounts.fetchStart())
    yield put(initSnacksQueue({ message: `Fetch ok.`, type: 'success' }))
  }
}
